import React, { useRef } from 'react';

import { Button } from '@lightspeed/flame/Button';
import { Box } from '@lightspeed/flame/Core';
import { Input } from '@lightspeed/flame/Input';
import { Radio } from '@lightspeed/flame/Radio';
import { Select } from '@lightspeed/flame/Select';
import { Text } from '@lightspeed/flame/Text';
import { Tooltip } from '@lightspeed/flame/Tooltip';

import { createReactRenderer } from '../helpers/react-renderer';

import { StyledFlex, StyledRadioLabel } from './CreditAndDepositRefund.style';
import { CreditAndDepositRefundProps } from './types';

const CreditAndDepositRefund: React.FC<CreditAndDepositRefundProps> = ({
  canRefundCreditAccount,
  paymentTypes,
  maxStr,
  max,
  ccPaymentOptions,
  isDeposit,
}) => {
  const accountWithdrawAmountRef = useRef<HTMLInputElement>(null);
  const accountWithdrawTypeRef = useRef<HTMLSelectElement>(null);
  const currencySymbol = maxStr.replace(/[\d., ]/g, '');
  const [helperErrorMsg, setHelperErrorMsg] = React.useState(false);

  const NotEnoughCredit = (
    <span style={{ color: '#DE2E37' }}>The maximum amount that can be refunded is {maxStr}</span>
  );

  const validationError = () => {
    if (accountWithdrawAmountRef.current) {
      const amount = parseFloat(accountWithdrawAmountRef.current.value);
      setHelperErrorMsg(amount > max);
    }
  };

  const handleRefund = () => {
    if (accountWithdrawAmountRef.current?.id && accountWithdrawTypeRef.current?.id) {
      window.merchantos.register.account_actions.addWithdraw(
        accountWithdrawAmountRef.current.id,
        accountWithdrawTypeRef.current.id,
        max
      );
    }
  };

  return (
    <>
      <StyledFlex>
        <Box>
          <strong>Type of refund</strong>
        </Box>
        <Box>
          {canRefundCreditAccount === true ? (
            <StyledRadioLabel description="Payments made to the credit account balance">
              <Radio
                data-test="CreditAccountPaymentRadio"
                defaultChecked={canRefundCreditAccount && !isDeposit}
                id="CreditAccountPaymentRadio"
                name="RefundPaymentType"
                value=""
                label={<Text>Credit account payment</Text>}
              />
            </StyledRadioLabel>
          ) : (
            <StyledRadioLabel description="Payments made to the credit account balance">
              <Radio
                data-test="CreditAccountPaymentRadioDisabled"
                disabled
                id="CreditAccountPaymentRadioDisabled"
                label={
                  <Tooltip
                    key="permission-tooltip"
                    placement="top"
                    content="You don't have permission to refund credit"
                  >
                    <Text>Credit account payment</Text>
                  </Tooltip>
                }
              />
            </StyledRadioLabel>
          )}
          <StyledRadioLabel mt="3" description="Deposits made on orders on layaway">
            <Radio
              data-test="DepositPaymentRadio"
              defaultChecked={!canRefundCreditAccount || isDeposit}
              id="DepositPaymentRadio"
              name="RefundPaymentType"
              value=""
              label={<Text>Deposit</Text>}
            />
          </StyledRadioLabel>
        </Box>
      </StyledFlex>
      <StyledFlex>
        <Box>
          <strong>Refund to</strong>
        </Box>
        <Box className="number-container-box">
          <Select ref={accountWithdrawTypeRef} id="account_withdraw_type" data-test="withdraw-type">
            {ccPaymentOptions.map((ccPaymentOption) => {
              const cardLabel = ccPaymentOption.isRemote
                ? ccPaymentOption.cardNumber
                : `${ccPaymentOption.cardBrand} ending in ${ccPaymentOption.cardNumber.slice(-4)}`;

              return (
                <option
                  key={ccPaymentOption.id}
                  value={`credit_card.${ccPaymentOption.id}.${ccPaymentOption.maxRefund}`}
                  data-paymentid={ccPaymentOption.paymentId || ''}
                  data-cardtype={ccPaymentOption.cardType}
                  data-isremote={ccPaymentOption.isRemote}
                  disabled={!ccPaymentOption.isSupported}
                >
                  {cardLabel} {`${ccPaymentOption.maxRefundMoneyValue}`} on deposit{' '}
                  {ccPaymentOption.isSupported ? '' : '[Not supported]'}
                </option>
              );
            })}
            {paymentTypes.map((paymentType) => (
              <option key={paymentType.id} value={paymentType.id}>
                {paymentType.description}
              </option>
            ))}
          </Select>
        </Box>
      </StyledFlex>
      <StyledFlex>
        <Box>
          <strong>Refund Amount</strong>
        </Box>
        <Box className="number-container-box">
          <Input
            data-test="withdraw-amount"
            id="account_withdraw_amount"
            type="number"
            prefix={currencySymbol}
            max={max}
            ref={accountWithdrawAmountRef}
            textHelper={helperErrorMsg ? NotEnoughCredit : `Maximum amount: ${maxStr}`}
            onBlur={validationError}
          />
        </Box>
      </StyledFlex>
      <StyledFlex>
        <Button data-test="refund-button" type="button" mr={1} onClick={handleRefund}>
          Ok
        </Button>
        <Button type="button" onClick={() => window.merchantos.register.account_actions.close()}>
          Cancel
        </Button>
      </StyledFlex>
    </>
  );
};

export default CreditAndDepositRefund;

const RenderedComponent = createReactRenderer({
  component: CreditAndDepositRefund,
  selector: '[data-react-root="CreditAndDepositRefund"]',
});

history.onChangeState(
  { pathname: '/register.php', form_name: undefined },
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);
